import { z } from 'zod';
import {
  AuditFieldsSchema,
  MoneyHufSchema,
  TenantIdSchema,
  TimestampSchema,
} from '../types/common.types';
import { PaginationQuerySchema } from '../types/pagination.types';

/**
 * 35-kör BE-5: Banki tétel + számla-párosítás (1:N) — ÚJ-wire BE↔FE single-source.
 * A FE bank-import (köv. fázis) EZT importálja (a 34-kör invoice-drift ellen: a wire ott
 * születik, ahol a DTO). R-1 ratified: 1:N párosítás (egy tétel több számlát fedhet).
 */

/** Párosítás-állapotgép (R-1): UNMATCHED → PARTIALLY_MATCHED → MATCHED → RECONCILED. */
export const BankMatchStatusSchema = z.enum([
  'UNMATCHED',
  'PARTIALLY_MATCHED',
  'MATCHED',
  'RECONCILED',
]);
export type BankMatchStatus = z.infer<typeof BankMatchStatusSchema>;

/** Banki mozgás iránya. */
export const BankDirectionSchema = z.enum(['IN', 'OUT']);
export type BankDirection = z.infer<typeof BankDirectionSchema>;

/** Tétel-eredet: kézi rögzítés vagy (P2) import. */
export const BankSourceSchema = z.enum(['MANUAL', 'IMPORT']);
export type BankSource = z.infer<typeof BankSourceSchema>;

/** Banki tétel wire-DTO (a pénzügyi zárás bank-párosítás magja). */
export const BankTransactionSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: TenantIdSchema,
    accountRef: z.string().max(50).nullable(),
    bookingDate: TimestampSchema,
    amount: MoneyHufSchema,
    currency: z.string().length(3),
    direction: BankDirectionSchema,
    counterpartyName: z.string().max(255).nullable(),
    counterpartyAccount: z.string().max(50).nullable(),
    memo: z.string().nullable(),
    source: BankSourceSchema,
    matchStatus: BankMatchStatusSchema,
  })
  .merge(AuditFieldsSchema);
export type BankTransaction = z.infer<typeof BankTransactionSchema>;

/** Bank-tétel ↔ számla párosítás (1:N join). A Match immutable: createdBy, NINCS updatedBy. */
export const BankTransactionMatchSchema = z.object({
  id: z.string().uuid(),
  tenantId: TenantIdSchema,
  bankTransactionId: z.string().uuid(),
  invoiceId: z.string().uuid(),
  matchedAmount: MoneyHufSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  createdBy: z.string().uuid().nullable(),
});
export type BankTransactionMatch = z.infer<typeof BankTransactionMatchSchema>;

/** Create-DTO: kézi banki tétel rögzítés (a párosítás külön endpoint). */
export const CreateBankTransactionSchema = z.object({
  accountRef: z.string().max(50).optional(),
  bookingDate: TimestampSchema,
  amount: MoneyHufSchema,
  currency: z.string().length(3).optional(),
  direction: BankDirectionSchema,
  counterpartyName: z.string().max(255).optional(),
  counterpartyAccount: z.string().max(50).optional(),
  memo: z.string().optional(),
});
export type CreateBankTransaction = z.infer<typeof CreateBankTransactionSchema>;

/** Match-DTO: a banki tétel egy számlához rendelése (1:N, részleges-fedezet alapja). */
export const MatchBankTransactionSchema = z.object({
  invoiceId: z.string().uuid(),
  matchedAmount: MoneyHufSchema,
});
export type MatchBankTransaction = z.infer<typeof MatchBankTransactionSchema>;

/** List-query: pagination + match-status / irány szűrő. */
export const BankTransactionListQuerySchema = PaginationQuerySchema.extend({
  matchStatus: BankMatchStatusSchema.optional(),
  direction: BankDirectionSchema.optional(),
});
export type BankTransactionListQuery = z.infer<typeof BankTransactionListQuerySchema>;
